import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

import { API_URL } from '../utils/api';

// Safari-safe POST request using XMLHttpRequest
// This avoids the "body is disturbed or locked" error on iOS Safari
function safariSafePost(url, body) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          data
        });
      } catch (e) {
        reject(new Error('Failed to parse response'));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error - please check your connection'));
    };
    
    xhr.ontimeout = function() {
      reject(new Error('Request timeout - please try again'));
    };
    
    xhr.timeout = 30000;
    xhr.send(JSON.stringify(body));
  });
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers,
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('token');
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const url = `${API_URL}/api/auth/login`;
      
      // Use XMLHttpRequest for Safari/iOS compatibility
      const response = await safariSafePost(url, { email, password });
      
      if (response.ok) {
        // Store token in localStorage for API calls
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        setUser(response.data);
        return response.data;
      } else {
        throw new Error(response.data.detail || 'Login failed');
      }
    } catch (error) {
      throw error;
    }
  };

  const register = async (name, email, password) => {
    try {
      // Use XMLHttpRequest for Safari/iOS compatibility
      const response = await safariSafePost(`${API_URL}/api/auth/register`, { name, email, password });
      
      if (response.ok) {
        // Store token in localStorage for API calls
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        setUser(response.data);
        return response.data;
      } else {
        throw new Error(response.data.detail || 'Registration failed');
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear token from localStorage
    localStorage.removeItem('token');
    setUser(null);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    setUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
